import React, { useEffect, useState } from 'react';

import { message } from 'antd';
import {
  DELETE_CONFIRM,
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';

import { useDrawers } from 'hooks/useDrawers';
import { useQueryParam } from 'hooks/useQueryParam';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { StoriesService } from 'services/StoriesService';
import { StoryGroupService } from 'services/StoryGroupService';

import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import { StoriesTable } from './StoriesTable';
import { StoriesForm } from './StoriesForm';

function Stories() {
  useDocumentTitle('Все истории');
  const { setCount } = useHeaderChanger('Все истории');
  const paramGroupId = useQueryParam('groupId');

  const [stories, setStories] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [storyGroups, setStoryGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { page, totalRows, perPage, setTotalRows, setPagination } =
    usePagination();

  useEffect(() => {
    setGroupId(+paramGroupId);
  }, [paramGroupId]);

  useEffect(() => {
    fetchStoryGroups();
  }, []);

  useEffect(() => {
    if (groupId) {
      fetchStories();
    }
  }, [groupId]);

  useEffect(() => {
    fetchStories();
  }, [page, perPage]);

  const fetchStories = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await StoriesService.getStories(
        groupId,
        page,
        perPage
      );

      setStories(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoryGroups = async () => {
    try {
      const data = await StoryGroupService.getAllStoryGroups();

      setStoryGroups(data);
    } catch (err) {
      message.error('Не удалось загрузить группы сторисов');
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await StoriesService.deleteStories(activeRecord.id);

      if (isDeleted) {
        closeDrawer();
        fetchStories();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  const filterGroup = (groupId) => {
    if (!groupId) {
      setGroupId(null);
    }

    setGroupId(groupId);
  };

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="stories" />

      <StoriesTable
        items={stories}
        storyGroups={storyGroups}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        onRowClick={openDrawer}
        onGroupFilter={filterGroup}
        onChange={setPagination}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.title}
        pageId="stories"
        recordFields={[
          {
            label: 'Группа истории',
            key: 'story_group_id',
            computedValue: storyGroups?.find(
              (group) => group.id === activeRecord?.story_group_id
            )?.title,
          },
          { label: 'Заголовок', key: 'title' },
          { label: 'Заголовок (каз)', key: 'title_kz' },
          { label: 'Описание', key: 'description' },
          { label: 'Описание (каз)', key: 'description_kz' },
          { label: 'Текст', key: 'text' },
          { label: 'Текст (каз)', key: 'text_kz' },
          { label: 'Цвет текста', key: 'text_color' },
          { label: 'Порядок', key: 'position' },
          { label: 'Фоновый цвет', key: 'background_color' },
          { label: 'SVG файл', key: 'svg' },
          { label: 'Медиа', key: 'image' },
          { label: 'Медиа (каз.)', key: 'image_kz' },
        ]}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText={DELETE_CONFIRM}
      />

      <StoriesForm
        isActive={formIsActive}
        initialValues={activeRecord}
        storyGroups={storyGroups}
        onSuccess={fetchStories}
        onClose={closeForm}
      />
    </>
  );
}

export { Stories };
